import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { api } from "./api.js";
import { InvitePage } from "./pages/InvitePage.js";
import { JobsPage } from "./pages/JobsPage.js";
import { LegalPage } from "./pages/LegalPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { AppShell } from "./components/AppShell.js";
import type { Me } from "./types.js";

function AuthenticatedApp({ me, refreshMe }: { me: Me; refreshMe: () => Promise<void> }) {
  return (
    <AppShell me={me} refreshMe={refreshMe}>
      <Routes>
        <Route path="/" element={<Navigate to="/jobs" replace />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/settings" element={<SettingsPage me={me} refreshMe={refreshMe} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AppShell>
  );
}

export function App() {
  const location = useLocation();
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const publicPath =
    location.pathname.startsWith("/invite/") || ["/privacy", "/terms"].includes(location.pathname);

  const refreshMe = useCallback(async () => {
    try {
      setMe(await api<Me>("/api/internal/me"));
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (publicPath) return;
    const timeout = window.setTimeout(() => void refreshMe(), 0);
    return () => window.clearTimeout(timeout);
  }, [publicPath, refreshMe]);

  if (publicPath) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage onSignedIn={refreshMe} />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    );
  }
  if (loading)
    return (
      <div className="app-loading" role="status">
        <span className="brand-tile">R</span>
        <span>正在打开工作区</span>
      </div>
    );
  if (!me) return <LoginPage onSignedIn={refreshMe} />;
  return <AuthenticatedApp me={me} refreshMe={refreshMe} />;
}
